import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Row, Col, Table, Badge, Spinner, Form, ButtonGroup, Button } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import CountUp from 'react-countup';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faSyncAlt,
    faDollarSign,
    faChartLine
} from '@fortawesome/free-solid-svg-icons';

import useToken from 'hooks/useToken';

registerLocale('es', es);
echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const getLocalDateISO = (d) => {
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
    }).format(num);
};

const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = cleanDate.split('-');
    if (parts.length === 3) {
        const d = new Date(parts[0], parts[1] - 1, parts[2]);
        return d.toLocaleDateString('es-MX', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }
    return cleanDate;
};

// StatCard idéntica a AkasiaPosStatsCards
const StatCard = ({ title, value, subValue, subLabel, color, icon, isCurrency }) => (
    <Card className="h-100 shadow-sm border-0 bg-white">
        <Card.Body className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-center h-100">
                <div className="flex-grow-1">
                    <p className="text-muted mb-1 text-uppercase fw-bold text-nowrap" style={{ fontSize: '0.65rem' }}>
                        {title}
                    </p>
                    <h4 className={`text-${color} mb-0 fw-bold lh-1`}>
                        {isCurrency ? (
                            <>
                                $<CountUp end={value || 0} duration={1.2} separator="," decimals={2} decimal="." />
                            </>
                        ) : (
                            <CountUp end={value || 0} duration={1.2} separator="," />
                        )}
                    </h4>
                    {subValue !== undefined && subValue !== null && (
                        <div className={`text-${color} small mt-1 opacity-75 fw-bold`} style={{ fontSize: '0.75rem' }}>
                            <span className="me-1">+</span>
                            <CountUp end={subValue} duration={1.2} separator="," />
                            <span className="ms-1">{subLabel || ''}</span>
                        </div>
                    )}
                </div>
                {icon && (
                    <div
                        className={`bg-${color} bg-opacity-10 p-2 rounded-3 d-flex align-items-center justify-content-center ms-2`}
                        style={{ width: '44px', height: '44px', minWidth: '44px' }}
                    >
                        <FontAwesomeIcon icon={icon} className={`text-${color}`} style={{ fontSize: '18px' }} />
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
);

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    subValue: PropTypes.number,
    subLabel: PropTypes.string,
    color: PropTypes.string.isRequired,
    icon: PropTypes.object,
    isCurrency: PropTypes.bool
};

const AkasiaPosVentasRenovaciones = () => {
    const { token } = useToken();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filterMode, setFilterMode] = useState('month'); // 'month' | 'range'
    const [selectedMonth, setSelectedMonth] = useState(new Date());

    // Fechas por defecto: mes actual completo
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const handleMonthChange = (date) => {
        if (!date) return;
        setSelectedMonth(date);
        const y = date.getFullYear();
        const m = date.getMonth();
        const firstDay = new Date(y, m, 1);
        const lastDay = new Date(y, m + 1, 0);
        setStartDate(getLocalDateISO(firstDay));
        setEndDate(getLocalDateISO(lastDay));
    };

    useEffect(() => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setSelectedMonth(today);
        setStartDate(getLocalDateISO(firstDay));
        setEndDate(getLocalDateISO(lastDay));
    }, []);

    const fetchData = useCallback(async (start, end) => {
        if (!token || !start || !end) return;
        setLoading(true);
        try {
            const res = await fetch('/api/AkasiaPosDashboard/GetVentasRenovacionesDiarias', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    startDate: start,
                    endDate: end
                })
            });

            if (res.ok) {
                const result = await res.json();
                if (result.iResponseType === 1 && result.detalles) {
                    const parsed = JSON.parse(result.detalles);
                    const rows = parsed.table2 || parsed.Table2 || parsed.table1 || parsed.Table1 || [];
                    setData(rows);
                } else {
                    setData([]);
                }
            }
        } catch (err) {
            console.error('Error al cargar ventas y renovaciones:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token && startDate && endDate) {
            fetchData(startDate, endDate);
        }
    }, [token, startDate, endDate, fetchData]);

    // Totales calculados para las StatCards
    const totals = useMemo(() => {
        return data.reduce(
            (acc, curr) => {
                acc.primerasVentas += Number(curr.iTotalPrimerasVentas) || 0;
                acc.montoPrimeras += Number(curr.dMontoPrimerasVentas) || 0;
                acc.renovaciones += Number(curr.iTotalRenovaciones) || 0;
                acc.montoRenovaciones += Number(curr.dMontoRenovaciones) || 0;
                acc.totalVentas += Number(curr.iTotalVentas) || 0;
                acc.montoTotal += Number(curr.dMontoTotal) || 0;
                return acc;
            },
            {
                primerasVentas: 0,
                montoPrimeras: 0,
                renovaciones: 0,
                montoRenovaciones: 0,
                totalVentas: 0,
                montoTotal: 0
            }
        );
    }, [data]);

    // Opciones del gráfico ECharts idéntico a la imagen de referencia (Barras apiladas + Línea suave)
    const chartOptions = useMemo(() => {
        if (!data || data.length === 0) return {};

        const categories = data.map((item) => {
            const clean = item.dFecha ? (item.dFecha.includes('T') ? item.dFecha.split('T')[0] : item.dFecha) : '';
            const parts = clean.split('-');
            if (parts.length === 3) {
                const d = new Date(parts[0], parts[1] - 1, parts[2]);
                return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
            }
            return clean;
        });

        const seriesPrimeras = data.map((item) => Number(item.dMontoPrimerasVentas) || 0);
        const seriesRenovaciones = data.map((item) => Number(item.dMontoRenovaciones) || 0);
        const seriesTotal = data.map((item) => Number(item.dMontoTotal) || 0);

        return {
            tooltip: {
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                borderColor: '#e9ecef',
                borderWidth: 1,
                padding: [10, 14],
                textStyle: { color: '#212529', fontSize: 12 },
                axisPointer: {
                    type: 'shadow',
                    shadowStyle: {
                        color: 'rgba(94, 53, 177, 0.1)',
                        borderRadius: 6
                    }
                },
                formatter: (params) => {
                    if (!params || !params.length) return '';
                    const dateIndex = params[0].dataIndex;
                    const item = data[dateIndex];
                    const dateStr = item?.dFecha ? formatDateDisplay(item.dFecha) : '';

                    return `
                        <div class="fw-bold mb-2 pb-1 border-bottom text-dark" style="font-size: 0.85rem;">${dateStr}</div>
                        <div class="d-flex justify-content-between gap-4 mb-1" style="color: #2563eb;">
                            <span><span style="display:inline-block;width:9px;height:9px;background:#2563eb;border-radius:2px;margin-right:6px;"></span>1ra Compra:</span>
                            <b>${formatCurrency(item?.dMontoPrimerasVentas)} (${item?.iTotalPrimerasVentas || 0})</b>
                        </div>
                        <div class="d-flex justify-content-between gap-4 mb-1" style="color: #d97706;">
                            <span><span style="display:inline-block;width:9px;height:9px;background:#d97706;border-radius:2px;margin-right:6px;"></span>Renovaciones:</span>
                            <b>${formatCurrency(item?.dMontoRenovaciones)} (${item?.iTotalRenovaciones || 0})</b>
                        </div>
                        <div class="d-flex justify-content-between gap-4 pt-1 mt-1 border-top fw-bold" style="color: #7c3aed;">
                            <span>Total del Día:</span>
                            <span>${formatCurrency(item?.dMontoTotal)} (${item?.iTotalVentas || 0} órds.)</span>
                        </div>
                    `;
                }
            },
            legend: {
                top: 0,
                right: 10,
                itemWidth: 12,
                itemHeight: 10,
                textStyle: { fontSize: 11, color: '#6c757d' },
                data: ['Primeras Compras', 'Renovaciones', 'Tendencia Total']
            },
            grid: {
                left: '2%',
                right: '2%',
                bottom: '5%',
                top: '12%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: categories,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#8fa0b5',
                    fontSize: 11,
                    interval: categories.length > 25 ? 'auto' : 0
                }
            },
            yAxis: {
                type: 'value',
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#8fa0b5',
                    fontSize: 11,
                    formatter: (val) => '$' + (val >= 1000 ? (val / 1000).toFixed(0) + 'k' : val)
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: 'dashed',
                        color: '#edf2f7'
                    }
                }
            },
            series: [
                // Barra inferior: Primeras Compras (Azul eléctrico vibrante)
                {
                    name: 'Primeras Compras',
                    type: 'bar',
                    stack: 'ventas',
                    barWidth: 14,
                    data: seriesPrimeras,
                    itemStyle: {
                        color: '#2563eb'
                    }
                },
                // Barra superior: Renovaciones (Ámbar / Naranja cálido bien diferenciable)
                {
                    name: 'Renovaciones',
                    type: 'bar',
                    stack: 'ventas',
                    barWidth: 14,
                    data: seriesRenovaciones,
                    itemStyle: {
                        color: '#f59e0b',
                        borderRadius: [3, 3, 0, 0]
                    }
                },
                // Línea de tendencia superior: Total (Púrpura / Violeta vibrante)
                {
                    name: 'Tendencia Total',
                    type: 'line',
                    data: seriesTotal,
                    smooth: 0.35,
                    showSymbol: false,
                    symbol: 'circle',
                    symbolSize: 6,
                    lineStyle: {
                        color: '#7c3aed',
                        width: 2.5
                    },
                    itemStyle: {
                        color: '#7c3aed'
                    }
                }
            ]
        };
    }, [data]);

    return (
        <div className="mb-4">
            {/* Stat Cards - Diseño idéntico a AkasiaPosStatsCards */}
            <Row className="g-3 mb-3">
                <Col xs={6} md={3}>
                    <StatCard
                        title="Ventas Totales"
                        value={totals.montoTotal}
                        subValue={totals.totalVentas}
                        subLabel="órdenes"
                        color="success"
                        isCurrency={true}
                        icon={faDollarSign}
                    />
                </Col>
                <Col xs={6} md={3}>
                    <StatCard
                        title="Primeras Compras"
                        value={totals.montoPrimeras}
                        subValue={totals.primerasVentas}
                        subLabel="órdenes"
                        color="primary"
                        isCurrency={true}
                        icon={faShoppingCart}
                    />
                </Col>
                <Col xs={6} md={3}>
                    <StatCard
                        title="Renovaciones"
                        value={totals.montoRenovaciones}
                        subValue={totals.renovaciones}
                        subLabel="órdenes"
                        color="warning"
                        isCurrency={true}
                        icon={faSyncAlt}
                    />
                </Col>
                <Col xs={6} md={3}>
                    <StatCard
                        title="Total Licencias POS"
                        value={totals.totalVentas}
                        subValue={
                            totals.totalVentas > 0
                                ? Math.round((totals.renovaciones / totals.totalVentas) * 100)
                                : 0
                        }
                        subLabel="% renovaciones"
                        color="dark"
                        icon={faChartLine}
                    />
                </Col>
            </Row>

            {/* Gráfica Combo (Barras Apiladas + Línea) */}
            <Row className="g-3 mb-4 mt-1">
                <Col lg={12}>
                    <Card className="h-100 shadow-sm border-0 bg-white">
                        <Card.Header className="bg-white border-bottom py-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <h6 className="mb-0 fw-bold">Actividad de Ventas y Renovaciones</h6>

                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <ButtonGroup size="sm">
                                    <Button
                                        variant={filterMode === 'month' ? 'primary' : 'outline-secondary'}
                                        onClick={() => setFilterMode('month')}
                                        className="py-1 px-2"
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        Mes
                                    </Button>
                                    <Button
                                        variant={filterMode === 'range' ? 'primary' : 'outline-secondary'}
                                        onClick={() => setFilterMode('range')}
                                        className="py-1 px-2"
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        Rango
                                    </Button>
                                </ButtonGroup>

                                {filterMode === 'month' ? (
                                    <div className="d-flex align-items-center">
                                        <DatePicker
                                            selected={selectedMonth}
                                            onChange={handleMonthChange}
                                            dateFormat="MMMM yyyy"
                                            showMonthYearPicker
                                            locale="es"
                                            className="form-control form-control-sm text-capitalize fw-bold text-center"
                                            style={{ width: '150px' }}
                                        />
                                    </div>
                                ) : (
                                    <div className="d-flex gap-1 align-items-center">
                                        <DatePicker
                                            selected={startDate ? new Date(startDate + 'T00:00:00') : null}
                                            onChange={(date) => setStartDate(date ? getLocalDateISO(date) : '')}
                                            className="form-control form-control-sm"
                                            style={{ width: '120px' }}
                                            dateFormat="dd MMM yyyy"
                                            locale="es"
                                            placeholderText="Inicio"
                                        />
                                        <span className="text-muted">-</span>
                                        <DatePicker
                                            selected={endDate ? new Date(endDate + 'T00:00:00') : null}
                                            onChange={(date) => setEndDate(date ? getLocalDateISO(date) : '')}
                                            className="form-control form-control-sm"
                                            style={{ width: '120px' }}
                                            dateFormat="dd MMM yyyy"
                                            locale="es"
                                            placeholderText="Fin"
                                        />
                                    </div>
                                )}
                            </div>
                        </Card.Header>

                        <Card.Body className="p-3">
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" size="sm" />
                                    <p className="text-muted small mt-2 mb-0">Cargando reporte de ventas...</p>
                                </div>
                            ) : data.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <p className="small mb-0">No hay registros de ventas en el período seleccionado.</p>
                                </div>
                            ) : (
                                <div style={{ height: '320px', width: '100%' }}>
                                    <ReactEChartsCore
                                        echarts={echarts}
                                        option={chartOptions}
                                        notMerge={true}
                                        lazyUpdate={true}
                                        style={{ height: '100%', width: '100%' }}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            
        </div>
    );
};

export default AkasiaPosVentasRenovaciones;
